<script module>
  import { setContext } from 'svelte'

  import { Client, gql } from '$lib/graphql.svelte.js'

  export const i18n = {
    en: {
      'Show %s more places': {
        one: 'Show one more place',
        other: 'Show %s more places'
      }
    },
    sv: {
      'Loading…': 'Laddar',
      'Type in the city you are looking for': 'Skriv in staden du letar efter',
      'We couldn’t find any places that matches your search term. Did you spell it correctly?':
        'Vi kunde inte hitta några platser som matchar din sökterm. Skrev du rätt?',
      'Show %s more places': {
        one: 'Visa en till plats',
        other: 'Visa %s fler platser'
      }
    }
  }

  export const LocationFragment = gql`
    fragment location on Location {
      uid
      name
      countryCode
      coordinates {
        type
        coordinates
      }
      image {
        url
        alt
      }
    }
  `

  /** @type {App.GraphQLOperation<{ locations: Schema.Destination[] }, { countryCode?: string, lang?: string }>} */
  export const FallbackQuery = gql`
    query Fallback($countryCode: String, $lang: String) {
      locations: findDestinations(
        country: $countryCode
        sortBy: "selected"
        lang: $lang
        limit: 6
      ) {
        location {
          ...${LocationFragment}
        }
      }
    }
  `

  /** @type {App.GraphQLOperation<{ autocomplete: { destinations: Schema.Destination[], locations: Schema.Location[] } }, { input: string, lang?: string }>} */
  export const AutocompleteQuery = gql`
    query Autocomplete($input: String!, $lang: String) {
      autocomplete(query: $input, lang: $lang) {
        destinations {
          location {
            ...${LocationFragment}
          }
        }
        locations {
          ...${LocationFragment}
        }
      }
    }
  `

  const client = new Client({ url: '/api/internal' })

  /** @param {string} [countryCode] */
  export function preload(countryCode) {
    // Populate the cache for the fallback query
    FallbackQuery.create(client).execute({ countryCode, lang: State.language })
  }

  export let AUTOCOMPLETE = globalThis.Symbol('autocomplete')

  class AutocompleteBinding extends EventTarget {
    offset = $state(0)
    value = $state('')
  }

  export function createAutocompleteBinding() {
    const binding = new AutocompleteBinding()

    setContext(AUTOCOMPLETE, binding)

    return (node) => {
      node.addEventListener('input', () => (binding.value = node.value.trim()))

      node.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          binding.offset = binding.offset + (event.key === 'ArrowDown' ? 1 : -1)
        } else if (event.key === 'Enter') {
          binding.dispatchEvent(new CustomEvent('enter'))
          event.preventDefault()
        } else if (event.key === 'Escape') {
          binding.dispatchEvent(new CustomEvent('escape'))
        }
      })

      $effect(() => {
        return () => {
          binding.offset = 0
          binding.value = ''
        }
      })
    }
  }
</script>

<script>
  import { throttle } from 'throttle-debounce'
  import { getContext } from 'svelte'

  import { i18n as countryNames } from '$lib/countries.js'
  import { register } from '$lib/utils/text.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import Symbol from '$lib/Symbol.svelte'
  import Image from '$lib/Image.svelte'

  let text = register(i18n, countryNames)

  /** @type {AutocompleteBinding} */
  const binding = getContext(AUTOCOMPLETE)

  /**
   * @type {{
   *   fallbackCountryCode?: string,
   *   name?: string,
   *   expanded?: boolean,
   *   onupdate?: function(): void,
   *   oncancel?: function(): void,
   *   onselect?: function(Schema.Location): void,
   * }}
   */
  let {
    fallbackCountryCode,
    name = 'location',
    expanded = false,
    onupdate,
    oncancel,
    onselect
  } = $props()

  const SearchResults = AutocompleteQuery.create(client)
  const FallbackResults = FallbackQuery.create(client)

  const throttledSearch = throttle(250, ({ input, lang }) => {
    SearchResults.execute({ input, lang })
  })

  $effect(() => {
    if (binding.value.length >= 2) {
      throttledSearch({ input: binding.value, lang: State.language })
    }
  })

  $effect(() =>
    FallbackResults.execute({
      countryCode: fallbackCountryCode,
      lang: State.language
    })
  )

  $effect.pre(() => {
    if (SearchResults.data) {
      onupdate?.()
    }
  })

  let primary = $derived(
    binding.value.length >= 2
      ? SearchResults.data?.autocomplete.destinations.map(
          ({ location }) => location
        )
      : FallbackResults?.data?.locations.map((location) =>
          // Handle legacy destination wrappers for promoted locations
          'location' in location ? location.location : location
        )
  )

  let secondary = $derived(
    SearchResults.data?.autocomplete?.locations.filter(
      (location) => !primary?.some((primary) => primary.uid === location.uid)
    )
  )

  let all = $derived(
    primary?.concat(
      (expanded || !primary?.length) && secondary ? secondary : []
    )
  )

  let selectedIndex = $derived(
    !all?.length
      ? 0
      : (binding.offset < 0 && binding.offset % all.length ? all.length : 0) +
          (binding.offset % all.length)
  )

  binding.addEventListener('escape', () => oncancel?.())
  binding.addEventListener('enter', () => {
    const selected = all?.[selectedIndex]
    if (selected) onselect?.(selected)
  })
</script>

<div class="autocomplete">
  <p class="heading" class:empty={!all?.length}>
    {#if !SearchResults?.data && !FallbackResults?.data}
      {#if SearchResults.fetching || FallbackResults?.fetching}
        {text`Loading…`}
      {:else if binding.value.length < 2}
        {text`Type in the city you are looking for`}
      {:else}
        {text`We couldn’t find any places that matches your search term. Did you spell it correctly?`}
      {/if}
    {:else if FallbackResults && !SearchResults.data}
      {#if fallbackCountryCode}
        {text`Other places in ${text(fallbackCountryCode)}`}
      {:else}
        {text`Popular places`}
      {/if}
    {:else}
      <span>{text`Search results`}</span>
    {/if}
  </p>
  {#if all?.length}
    <ol class="locations">
      {#each all as location, index}
        <li class="location">
          <button
            {name}
            class="option"
            class:selected={selectedIndex === index}
            value={location.uid}
            onclick={(event) => {
              onselect?.(location)
              event.preventDefault()
            }}>
            {#if location.image}
              <Image
                src={location.image.url}
                alt={location.image.alt || ''}
                class="icon"
                sizes="3.5rem"
                width={24}
                height={24}
                aspect={1}
                transforms="q_50"
                variants={[112]} />
            {:else}
              <div class="icon">
                <Symbol name="pin" size="sm" />
              </div>
            {/if}
            <span class="label">
              <strong>{location.name}</strong>
              <span class="country">
                {text(location.countryCode)}
              </span>
            </span>
          </button>
        </li>
      {/each}
    </ol>
  {/if}
  {#if secondary?.length}
    {#if !expanded && primary?.length}
      <button
        class="toggle"
        type="button"
        onclick={(event) => {
          onupdate?.()
          expanded = true
          event.preventDefault()
        }}>
        <Symbol name="chevron" prefix size="sm" />
        {text`Show ${secondary.length} more places`}
      </button>
    {/if}
  {/if}
</div>

<style>
  .autocomplete {
    width: 100%;
    background: var(--background);
    text-align: left;
    white-space: normal;
    letter-spacing: 0;
    user-select: none;
  }

  /** Search results */

  .locations {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .location {
    padding: 0;
    margin: 0;
  }

  .location:not(:last-child) {
    border-bottom: 0.5px solid rgba(var(--rgb-muted), 0.4);
  }

  .option {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.65rem 0;
    text-align: left;
    font-size: 1rem;
    font-weight: normal;
  }

  @media (width > 800px) {
    .option {
      padding: 0.5rem 0;
    }
  }

  .option.selected {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.15rem;
    text-decoration-skip-ink: none;
    text-decoration-color: rgb(var(--rgb-text));
  }

  .option :global(.icon) {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.2em;
    height: 1.2em;
    margin-right: 0.6rem;
    font-size: 1.25rem;
    contain: strict;
    user-select: none;
    vertical-align: text-bottom;
    border-radius: var(--border-radius-xs);
    background-color: rgb(var(--rgb-gray));
    color: rgb(var(--rgb-bright-gray));
  }

  .label {
    flex: 1 1 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  .country {
    color: rgb(var(--rgb-muted));
  }

  .toggle {
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    user-select: none;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
    text-underline-offset: 0.125rem;
    text-decoration-thickness: 1px;
  }

  .toggle:hover {
    text-decoration: underline;
  }

  .heading {
    display: block;
    margin: 0;
    font-weight: normal;
    font-size: 1rem;
    line-height: 1.45;
    text-wrap: balance;
  }

  .heading:empty {
    display: none;
  }

  .heading:not(.empty) {
    padding-bottom: 0.75rem;
    border-bottom: 0.5px solid rgba(var(--rgb-muted), 0.4);
  }
</style>
